import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerBadge } from "../lib/customers";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { searchCatalog, suggestedActions } from "../lib/searchCatalog";
import { formatDueDay } from "../lib/tasks";
import { CLAIM_STATUS_LABELS } from "../lib/claims";
import {
  Search, Building2, CheckSquare2, UserRound, ShieldCheck, UsersRound, Wallet, Briefcase, ListChecks, Receipt, CalendarClock, KeyRound,
  ArrowRight, Plus, PanelsTopLeft, Printer, FileText, GraduationCap, LifeBuoy, ReceiptText, Lightbulb, AlertTriangle, ShoppingCart, Package, Megaphone, Handshake, Gavel, CalendarDays, BookOpen,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { MobileSheet, MobileSheetContent, MobileSheetTitle } from "./ui/mobile-sheet";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { STAGE_LABEL } from "../lib/pipeline";
import { ISSUE_STATUS_META, SEVERITY_META } from "../lib/equipment";
import { STATUS_LABELS as TICKET_STATUS_LABELS } from "../lib/tickets";
import { DOCUMENT_KIND_LABELS, SUBSCRIPTION_STATUS_LABELS } from "../lib/billing";
import { INCIDENT_SEVERITY_LABELS, INCIDENT_STATE_LABELS, STATUS_LABELS as PRODUCT_STATUS_LABELS } from "../lib/product";
import { REQUEST_STATUS_LABELS, STATUS_LABELS as ASSET_STATUS_LABELS } from "../lib/procurement";
import { CAMPAIGN_STATE_LABELS, PARTNER_STATE_LABELS } from "../lib/marketing";
import { MEETING_STATUS_LABELS, STATUS_LABELS as DECISION_STATUS_LABELS, formatMoment } from "../lib/governance";
import { OBLIGATION_STATE_LABELS, STATE_LABELS as RECORD_STATE_LABELS } from "../lib/records";

const MIN_QUERY_LENGTH = 2;
const EMPTY_RESULTS = { prospects: [], tasks: [], users: [], pilots: [], collaborators: [], expenses: [], missions: [], procedures: [], training_programs: [], training_sessions: [], claims: [],
  subscriptions: [], equipment_issues: [], assist_notes: [], tickets: [], billing: [], records: [], product: [], procurement: [], marketing: [], governance: [], roles: [] };
const MISSION_STATUS = { draft: "Brouillon", planned: "Planifiée", in_progress: "En cours", completed: "Terminée", postponed: "Reportée",
  cancelled: "Annulée", interrupted: "Interrompue" };

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 639px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // Ctrl/Cmd+K opens the palette from anywhere in the app.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return () => controller.abort();
    }
    setResults(EMPTY_RESULTS);
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: trimmed }, signal: controller.signal });
        if (!controller.signal.aborted) setResults(data);
      } catch (e) {
        // Search-as-you-type is non-critical; an obsolete request is canceled.
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query, open]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setQuery("");
      setResults(EMPTY_RESULTS);
    }
  };

  // A choice closes the palette at once (new instance, no exit animation): while animating out it would stay the top
  // overlay and swallow the first Escape meant for whatever the destination opens (e.g. the task form).
  const [instance, setInstance] = useState(0);
  const go = (path, state) => {
    onOpenChange(false);
    setInstance((n) => n + 1);
    navigate(path, state ? { state } : undefined);
  };

  // An entry that opens a panel rather than a page (« Ouvrir le Copilote »).
  const openFromEvent = (name) => {
    onOpenChange(false);
    setInstance((n) => n + 1);
    window.dispatchEvent(new Event(name));
  };

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const hasResults = Object.values(results).some((items) => items?.length > 0);
  // Pages, tabs and actions: instant, from the first letter, filtered by the person's rights (lib/searchCatalog.js).
  const entries = useMemo(() => (trimmedQuery ? searchCatalog(user, trimmedQuery) : suggestedActions(user)), [user, trimmedQuery]);
  const catalogActions = entries.filter((e) => e.kind === "action");
  const catalogPages = entries.filter((e) => e.kind !== "action");
  const entryItem = (e) => (
    <CommandItem key={e.id} value={`go-${e.id}`} onSelect={() => (e.event ? openFromEvent(e.event) : go(e.path, e.state))} data-testid={`search-go-${e.id}`}>
      {e.kind === "action" ? <Plus size={16} className="text-slate-400" /> : <PanelsTopLeft size={16} className="text-slate-400" />}
      <span className="min-w-0 flex-1 truncate">{e.label}</span>
      <ArrowRight size={14} className="text-slate-300" />
    </CommandItem>
  );
  const dataItem = (key, value, Icon, title, subtitle, onSelect) => (
    <CommandItem key={key} value={value} onSelect={onSelect} data-testid={`search-result-${value}`}>
      <Icon size={16} className="text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="truncate">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate">{subtitle}</div>}
      </div>
    </CommandItem>
  );
  // Phone: the shared bottom sheet, opened to the whole screen (results need the room; DESIGN_SYSTEM.md §23). Computer: the dialog.
  const SearchOverlay = isMobile ? MobileSheet : Dialog;
  const SearchContent = isMobile ? MobileSheetContent : DialogContent;
  const SearchTitle = isMobile ? MobileSheetTitle : DialogTitle;

  return (
    <>
      <button
        type="button"
        data-testid="btn-global-search"
        onClick={() => setOpen(true)}
        className="flex-1 max-w-xl flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:border-slate-300 transition"
      >
        <Search size={16} className="text-slate-400 shrink-0" />
        <span className="flex-1 min-w-0 truncate text-left text-sm hidden sm:inline">
          Rechercher dans le CRM…
        </span>
        <span className="flex-1 min-w-0 truncate text-left text-sm sm:hidden">Rechercher…</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-2xs font-medium text-slate-400 shrink-0">
          Ctrl K
        </kbd>
      </button>

      <SearchOverlay key={instance} open={open} onOpenChange={onOpenChange}>
        <SearchContent
          {...(isMobile ? { "aria-describedby": undefined, closeOnHandle: true } : {})}
          className={isMobile ? "h-dvh max-h-none overflow-hidden rounded-none border-t-0 bg-white pt-[env(safe-area-inset-top,0px)]" : "overflow-hidden p-0 sm:max-w-xl"}
          data-testid="global-search-dialog"
        >
          <SearchTitle className="sr-only">Recherche globale</SearchTitle>
          <Command
            shouldFilter={false}
            className="min-h-0 [&_[cmdk-input-wrapper]]:pr-10 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2"
          >
            <CommandInput
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Pages, actions, pharmacies, tâches, missions…"
              data-testid="global-search-input"
            />
            <CommandList className={isMobile ? "min-h-0 flex-1 max-h-none pb-[env(safe-area-inset-bottom,0px)]" : undefined}>
              {!trimmedQuery && catalogActions.length > 0 && <CommandGroup heading="Actions rapides">{catalogActions.map(entryItem)}</CommandGroup>}
              {!trimmedQuery && (
                <div className="py-4 text-center text-xs text-slate-500">Cherchez une pharmacie, une tâche, une mission, une personne, une page ou une action.</div>
              )}
              {trimmedQuery && catalogActions.length > 0 && <CommandGroup heading="Actions">{catalogActions.map(entryItem)}</CommandGroup>}
              {trimmedQuery && catalogPages.length > 0 && <CommandGroup heading="Pages et onglets">{catalogPages.map(entryItem)}</CommandGroup>}
              {hasQuery && (
                <>
                  {results.prospects.length > 0 && (
                    <CommandGroup heading="Prospects">
                      {results.prospects.map((p) => dataItem(p.id, `prospect-${p.id}`, Building2, p.pharmacy_name,
                        [p.commune || p.region, STAGE_LABEL[p.status], customerBadge(p.customer)?.label].filter(Boolean).join(" · "), () => go(`/prospects/${p.id}`)))}
                    </CommandGroup>
                  )}
                  {results.pilots?.length > 0 && (
                    <CommandGroup heading="Pilotes">
                      {results.pilots.map((p) => dataItem(p.id, `pilot-${p.id}`, ShieldCheck, p.pharmacy_name, null, () => go(`/pilotes/${p.prospect_id}`)))}
                    </CommandGroup>
                  )}
                  {results.tasks.length > 0 && (
                    <CommandGroup heading="Tâches">
                      {results.tasks.map((t) => dataItem(t.id, `task-${t.id}`, CheckSquare2, t.title,
                        t.due_date ? `Échéance ${formatDueDay(t.due_date)}` : null, () => go("/tasks", { openTask: t.id })))}
                    </CommandGroup>
                  )}
                  {results.missions?.length > 0 && (
                    <CommandGroup heading="Missions">
                      {results.missions.map((m) => dataItem(m.id, `mission-${m.id}`, Briefcase, m.title || m.reference,
                        [m.reference, MISSION_STATUS[m.status]].filter(Boolean).join(" · "), () => go(`/missions/${m.id}`)))}
                    </CommandGroup>
                  )}
                  {results.equipment_issues?.length > 0 && (
                    <CommandGroup heading="Anomalies matérielles">
                      {results.equipment_issues.map((i) => dataItem(i.id, `equipment-issue-${i.id}`, Printer, i.title,
                        [i.number, SEVERITY_META[i.severity]?.label, ISSUE_STATUS_META[i.status]?.label].filter(Boolean).join(" · "),
                        () => go(`/equipements?anomalie=${i.id}`)))}
                    </CommandGroup>
                  )}
                  {results.tickets?.length > 0 && (
                    <CommandGroup heading="Tickets">
                      {results.tickets.map((t) => dataItem(t.id, `ticket-${t.id}`, LifeBuoy, t.title,
                        [t.number, t.pharmacy_name, TICKET_STATUS_LABELS[t.status]].filter(Boolean).join(" · "), () => go(`/support/tickets/${t.id}`)))}
                    </CommandGroup>
                  )}
                  {results.billing?.length > 0 && (
                    <CommandGroup heading="Revenus">
                      {results.billing.map((b) => dataItem(b.id, `billing-${b.id}`, ReceiptText,
                        b.kind === "subscription" ? `${b.pharmacy_name} · ${b.offer_name}` : `${DOCUMENT_KIND_LABELS[b.kind]} ${b.number}`,
                        [b.kind === "subscription" ? b.number : b.pharmacy_name,
                          b.kind === "subscription" ? SUBSCRIPTION_STATUS_LABELS[b.status] : b.status === "cancelled" ? "Annulé" : null].filter(Boolean).join(" · "),
                        () => go(`/revenus/abonnements/${b.subscription_id}`)))}
                    </CommandGroup>
                  )}
                  {results.records?.length > 0 && (
                    <CommandGroup heading="Documents et contrats">
                      {results.records.map((r) => (r.kind === "document"
                        ? dataItem(r.id, `record-${r.id}`, FileText, r.title, [r.number, RECORD_STATE_LABELS[r.state]].filter(Boolean).join(" · "),
                          () => go(`/documents/${r.id}`))
                        : r.kind === "clause"
                          ? dataItem(r.id, `record-clause-${r.id}`, FileText, r.title, ["Clause", r.theme, r.number ? `version ${r.number}` : null].filter(Boolean).join(" · "),
                            () => go(`/documents/clauses/${r.id}`))
                          : dataItem(r.id, `record-obligation-${r.id}`, FileText, r.title,
                          ["Obligation", r.due_date?.split("-").reverse().join("/"), OBLIGATION_STATE_LABELS[r.state]].filter(Boolean).join(" · "),
                          () => go(`/documents/obligations?obligation=${r.id}`))))}
                    </CommandGroup>
                  )}
                  {results.procurement?.length > 0 && (
                    <CommandGroup heading="Achats et actifs">
                      {results.procurement.map((r) => (r.kind === "request"
                        ? dataItem(r.id, `procurement-request-${r.id}`, ShoppingCart, r.title, [r.number, REQUEST_STATUS_LABELS[r.status]].filter(Boolean).join(" · "),
                          () => go(`/achats/demandes/${r.id}`))
                        : r.kind === "asset"
                          ? dataItem(r.id, `procurement-asset-${r.id}`, Package, r.title, [r.number, ASSET_STATUS_LABELS[r.status]].filter(Boolean).join(" · "),
                            () => go(`/achats/actifs/${r.id}`))
                          : dataItem(r.id, `procurement-supplier-${r.id}`, Building2, r.title, ["Fournisseur", r.number].join(" · "),
                            () => go(`/achats/fournisseurs?q=${encodeURIComponent(r.number)}`))))}
                    </CommandGroup>
                  )}
                  {results.marketing?.length > 0 && (
                    <CommandGroup heading="Marketing">
                      {results.marketing.map((r) => (r.kind === "campaign"
                        ? dataItem(r.id, `marketing-campaign-${r.id}`, Megaphone, r.name, [r.number, CAMPAIGN_STATE_LABELS[r.state]].filter(Boolean).join(" · "),
                          () => go(`/marketing/campagnes?q=${encodeURIComponent(r.number)}&state=all`))
                        : dataItem(r.id, `marketing-partner-${r.id}`, Handshake, r.name, ["Partenaire", r.number, PARTNER_STATE_LABELS[r.state]].filter(Boolean).join(" · "),
                          () => go(`/marketing/partenaires?q=${encodeURIComponent(r.number)}&state=all`))))}
                    </CommandGroup>
                  )}
                  {results.governance?.length > 0 && (
                    <CommandGroup heading="Gouvernance">
                      {results.governance.map((r) => {
                        if (r.kind === "decision") {
                          return dataItem(r.id, `governance-decision-${r.id}`, Gavel, r.title, ["Décision", r.number, DECISION_STATUS_LABELS[r.status]].filter(Boolean).join(" · "),
                            () => go(`/gouvernance/decisions/${r.id}`));
                        }
                        if (r.kind === "meeting") {
                          return dataItem(r.id, `governance-meeting-${r.id}`, CalendarDays, r.title, ["Réunion", r.number, MEETING_STATUS_LABELS[r.status], formatMoment(r.starts_at)].filter(Boolean).join(" · "),
                            () => go(`/gouvernance/reunions/${r.id}`));
                        }
                        if (r.kind === "risk") {
                          return dataItem(r.id, `governance-risk-${r.id}`, AlertTriangle, r.title, ["Risque", r.number].filter(Boolean).join(" · "), () => go(`/gouvernance/risques/${r.id}`));
                        }
                        return dataItem(r.id, `governance-article-${r.id}`, BookOpen, r.title, ["Article", r.number].filter(Boolean).join(" · "), () => go(`/support/connaissances/${r.id}`));
                      })}
                    </CommandGroup>
                  )}
                  {results.product?.length > 0 && (
                    <CommandGroup heading="Produit">
                      {results.product.map((r) => (r.kind === "item"
                        ? dataItem(r.id, `product-item-${r.id}`, Lightbulb, r.title, [r.number, PRODUCT_STATUS_LABELS[r.status]].filter(Boolean).join(" · "), () => go(`/produit/${r.id}`))
                        : dataItem(r.id, `product-incident-${r.id}`, AlertTriangle, r.title,
                          [r.number, INCIDENT_SEVERITY_LABELS[r.severity], INCIDENT_STATE_LABELS[r.state]].filter(Boolean).join(" · "), () => go(`/produit/incidents/${r.id}`))))}
                    </CommandGroup>
                  )}
                  {results.assist_notes?.length > 0 && (
                    <CommandGroup heading="Comptes rendus d'assistance">
                      {results.assist_notes.map((n) => dataItem(n.request_id, `assist-note-${n.request_id}`, FileText, n.pharmacy || "Compte rendu",
                        n.excerpt || null, () => go("/assistance", { openNote: n.request_id })))}
                    </CommandGroup>
                  )}
                  {results.procedures?.length > 0 && (
                    <CommandGroup heading="Procédures">
                      {results.procedures.map((p) => dataItem(p.id, `procedure-${p.id}`, ListChecks, p.label, p.active === false ? "Désactivée" : null,
                        () => go(`/missions/procedures/${p.id}`)))}
                    </CommandGroup>
                  )}
                  {results.training_sessions?.length > 0 && (
                    <CommandGroup heading="Sessions de formation">
                      {results.training_sessions.map((s) => dataItem(s.id, `training-session-${s.id}`, GraduationCap, s.program,
                        [s.reference, s.date, s.pharmacy].filter(Boolean).join(" · "), () => go(`/formations/sessions/${s.id}`)))}
                    </CommandGroup>
                  )}
                  {results.training_programs?.length > 0 && (
                    <CommandGroup heading="Programmes de formation">
                      {results.training_programs.map((p) => dataItem(p.id, `training-program-${p.id}`, GraduationCap, p.label,
                        [p.number ? `Version ${p.number}` : "Jamais publié", p.active === false ? "Désactivé" : null].filter(Boolean).join(" · "),
                        () => go(`/formations/programmes/${p.id}`)))}
                    </CommandGroup>
                  )}
                  {results.collaborators?.length > 0 && (
                    <CommandGroup heading="Collaborateurs">
                      {results.collaborators.map((c) => dataItem(c.id, `collaborator-${c.id}`, UsersRound, c.display_name, c.job_title, () => go(`/collaborateurs/${c.id}`)))}
                    </CommandGroup>
                  )}
                  {results.users.length > 0 && (
                    <CommandGroup heading="Utilisateurs">
                      {results.users.map((u) => dataItem(u.id, `user-${u.id}`, UserRound, u.name, u.email,
                        () => (u.id === user?.id ? go("/profile") : go("/users", { openUser: u.id }))))}
                    </CommandGroup>
                  )}
                  {results.roles?.length > 0 && (
                    <CommandGroup heading="Rôles">
                      {results.roles.map((r) => dataItem(r.id || r.name, `role-${r.name}`, KeyRound, r.label || r.name, null, () => go("/roles", { openRole: r.id })))}
                    </CommandGroup>
                  )}
                  {results.expenses?.length > 0 && (
                    <CommandGroup heading="Dépenses">
                      {results.expenses.map((e) => dataItem(e.id, `expense-${e.id}`, Wallet, e.title, e.supplier, () => go("/finance", { openExpense: e.id })))}
                    </CommandGroup>
                  )}
                  {results.claims?.length > 0 && (
                    <CommandGroup heading="Notes de frais">
                      {results.claims.map((c) => dataItem(c.id, `claim-${c.id}`, Receipt, c.title,
                        [c.claimant?.name, CLAIM_STATUS_LABELS[c.status]].filter(Boolean).join(" · "), () => go("/finance", { openClaim: c.id })))}
                    </CommandGroup>
                  )}
                  {results.subscriptions?.length > 0 && (
                    <CommandGroup heading="Abonnements">
                      {results.subscriptions.map((x) => dataItem(x.id, `subscription-${x.id}`, CalendarClock, x.name, x.supplier,
                        () => go("/finance?tab=subscriptions", { openSubscription: x.id })))}
                    </CommandGroup>
                  )}
                  {loading && <div className="py-3 text-center text-xs text-slate-500">Recherche dans les données…</div>}
                </>
              )}
              {trimmedQuery && !loading && entries.length === 0 && (!hasQuery || !hasResults) && (
                <CommandEmpty>Aucun résultat pour « {trimmedQuery} »</CommandEmpty>
              )}
            </CommandList>
          </Command>
        </SearchContent>
      </SearchOverlay>
    </>
  );
}
